// CSV-Export der Seite „Verkaufspreis" (Schnitt 1) für deutsches Excel: eine Zeile je
// Artikel × Stückzahl, alle Kundenversand-Optionen (inkl. `custom`) als Spaltenpaare.
import type { CarrierTier, KombiPreviewResult, KombiPreviewRow, SetCalcResult, SetPreviewResult, SetPreviewRow } from "@/api/pricing";
import { buildCsvDe, formatCsvDecimalDe } from "@/lib/csv";

/** Anzeige-Namen der Versandstufen (Tabelle und CSV). */
export const TIER_LABEL: Record<CarrierTier, string> = {
  auto: "auto",
  brief: "Brief",
  warenpost: "Warenpost",
  dhl: "DHL",
  gls: "GLS",
  gls_uebergroesse_150: "GLS Übergröße bis 150 cm",
  gls_uebergroesse_gt150: "GLS Übergröße über 150 cm",
};

/** Text statt Zahl, wenn der Nenner-Schutz greift — nie `0,00` (Plan P1). */
export const UNGUELTIG = "ungültig";

/** Datenqualitäts- und Rechen-Hinweise einer Zeile als Text (Reihenfolge fest). */
export function setHints(row: SetPreviewRow | KombiPreviewRow, set: SetCalcResult): string[] {
  const hints: string[] = [];
  if (row.ek_missing) hints.push("EK fehlt");
  if (row.ek_zero) hints.push("EK 0");
  if (row.weight_missing) hints.push("Gewicht fehlt");
  if (row.weight_suspect) hints.push("Gewicht Platzhalter?");
  if (!("key" in row) && row.dims_missing) hints.push("Maße fehlen");
  if (set.plus_versand_negativ) hints.push("Plus-Versand negativ (auf 0 gesetzt)");
  if (set.invalid) hints.push(UNGUELTIG);
  return hints;
}

export function buildVerkaufspreisCsv(result: SetPreviewResult | undefined, kombis?: KombiPreviewResult): string {
  const optionen = [...new Map([...(result?.kundenversand_optionen ?? []), ...(kombis?.kundenversand_optionen ?? [])].map(o => [o.key, o])).values()];
  const header = [
    "Sternnummer", "SKU", "Name", "Typ", "Grundartikel", "Marge %", "Stückzahl", "EK gesamt", "Gewicht g",
    "Versandstufe", "Versand", "Break-Even (Gratisversand)", "VK Marge",
    ...optionen.flatMap((o) => [`Plus-Versand (${o.label})`, `Kundenversand (${o.label})`]),
    "Hinweise",
  ];

  const rows: string[][] = [];
  for (const row of [...(result?.rows ?? []), ...(kombis?.rows ?? [])]) {
    for (const set of row.sets) {
      const preis = (n: number) => (set.invalid ? UNGUELTIG : formatCsvDecimalDe(n));
      rows.push([
        "key" in row ? row.label : row.merchant_sku ?? "",
        "key" in row ? row.components.map(c => `${c.sku} ×${c.qty}`).join(" + ") : row.sku,
        row.name,
        "key" in row ? "Kombi" : "Artikel",
        "key" in row ? "" : row.is_grundartikel ? "ja" : "nein",
        formatCsvDecimalDe(row.margin_pct * 100),
        String(set.quantity),
        formatCsvDecimalDe(set.ek_total),
        String(Math.round(set.weight_g_total)),
        TIER_LABEL[set.carrier_tier_used],
        preis(set.shipping),
        preis(set.break_even_inkl),
        preis(set.preis_inkl),
        ...optionen.flatMap((o) => {
          const plus = set.plus_versand[o.key];
          return [plus === undefined ? "" : preis(plus), formatCsvDecimalDe(o.betrag)];
        }),
        // `, ` statt `;` — die Zelle bleibt ungequotet und Excel-lesbar.
        setHints(row, set).join(", "),
      ]);
    }
  }
  return buildCsvDe(header, rows);
}

/** Dateinamen-Teil: Kleinbuchstaben, nur `[a-z0-9_-]`, Rest zu `_`, Folgen zusammengefasst. */
export function sanitizeFilePart(s: string): string {
  const cleaned = s
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return cleaned === "" ? "ordner" : cleaned;
}

/** `verkaufspreis_<ordnername>_<YYYY-MM-DD>.csv` mit dem lokalen Kalenderdatum. */
export function verkaufspreisFilename(folderName: string, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const datum = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return `verkaufspreis_${sanitizeFilePart(folderName)}_${datum}.csv`;
}
